import { SetPermanentJobSubmittalToInterviewDto } from '../models/set-permanent-job-submittal-to-interview-dto.model';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { PostOfferModalType } from 'src/app/accountmanagers/perm-post-offer/enums/post-offer-modal-type.enum';
import {
    SubmittalInterviewSubStatusEnum,
    SubmittalStatusEnum,
    SubmittalSubStatusEnum
} from '../../submittals/model/enums/submittal-status.enum';
import { PostOfferModalDiscoveryResponse } from '../models/post-offer-modal-discovery-response.model';
import { SetPermanentJobSubmittalToInterviewResultDto } from '../models/set-permanent-job-submittal-to-interview-result-dto.model';
import { Observable } from 'rxjs';
import { PermPostOfferInterviewModel } from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-interview.model';
import { AMSubmittalJob, SubmittalData } from '../../submittals/model/am-submittals.model';
import { SubmittalTypeEnum } from '../../submittals/model/am-submittals.enum';
import {
    PermanentJobOfferStatusEnum,
    PostOfferInformationSerializable
} from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-information.model';
import { PermPostOfferUpdateOfferPayload } from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-update-offer-payload.model';
import { PermPostOfferWorkflowSubmittalInformationExpanded } from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-workflow-submittal-info.model';
import { SetPermanentJobSubmittalToOfferResultDto } from '../models/set-permanent-job-submittal-to-offer-result-dto.model';
import { SetPermanentJobSubmittalToOfferDto } from '../models/set-permanent-job-submittal-to-offer-dto.model';
import { SubmittalJobStatus } from 'src/app/shared/models/enums/submittal-job-status.enum';
import { PermanentPlacementNote } from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-placement-note.model';
import { PostOfferModalMetadata } from 'src/app/root-store/perm-post-offer-workflow-store/models/post-offer-modal-metadata.model';
import { UpdatePermanentJobOfferStatusDto } from '../models/update-job-offer-dashboard-status-dto.model';
import { PermanentJobOfferDashboardStatusEnum } from '../enums/permanent-job-offer-dashboard-status.enum';
import { PostOfferBillingInformation } from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-billing-information.model';

export class PermPostOfferAPIUrls {
    static SetPermanentJobSubmittalToInterviewUrl(submittalId: number, jobId: number): string {
        return `AyaNova/PermanentJobSubmittal/${submittalId}/jobs/${jobId}/status/interview`;
    }

    static GetPermPlacementNotesUrl(facilityId: number): string {
        return `AyaNova/PermanentJobSubmittal/facility/${facilityId}/note`;
    }

    static GetOfferInformationUrl(submittalJobOrVendorSubmittalId: number, submittalType: SubmittalTypeEnum): string {
        return `AyaNova/PermanentJobSubmittal/offer/${submittalJobOrVendorSubmittalId}?submittalType=${submittalType}`;
    }

    static SetOfferInformationUrl(submittalId: number, jobId: number): string {
        return `AyaNova/PermanentJobSubmittal/${submittalId}/jobs/${jobId}/status/offer`;
    }

    static PermanentJobOfferUrl(jobOfferId: number): string {
        return `AyaNova/account-manager-candidates/permanent/offer/${jobOfferId}/status`;
    }

    static UpdatePermPostOfferBillingInfo(): string {
        return 'AyaNova/PermanentJobSubmittal/update-billing';
    }
}

export class PermPostOfferLinks {
    static FacilityLink(facilityId: number): string {
        return `/accountmanagers/facilities/${facilityId}`;
    }

    static CandidateLink(submittalType: SubmittalTypeEnum, candidateId: number): string {
        return submittalType === SubmittalTypeEnum.Aya
            ? `#/recruiting/candidates/${candidateId}/new-profile/about`
            : `#/partnernetwork/candidate/${candidateId}?subcontractor=1`;
    }

    static CandidateLinkByContractorStatus(isSubcontractor: boolean, candidateId: number): string {
        return isSubcontractor
            ? this.CandidateLink(SubmittalTypeEnum.APN, candidateId)
            : this.CandidateLink(SubmittalTypeEnum.Aya, candidateId);
    }

    static JobLink(jobId: number): string {
        return `/accountmanagers/jobs/${jobId}`;
    }
}

@Injectable({
    providedIn: 'root'
})
export class PermPostOfferService {
    setPermanentJobSubmittalToInterview(
        submittalId: number,
        jobId: number,
        dto: SetPermanentJobSubmittalToInterviewDto
    ): Observable<SetPermanentJobSubmittalToInterviewResultDto> {
        const url = `${this._settings.coreAPIUrl}${PermPostOfferAPIUrls.SetPermanentJobSubmittalToInterviewUrl(submittalId, jobId)}`;
        return this._http.post<SetPermanentJobSubmittalToInterviewResultDto>(url, dto);
    }

    getPermPlacementNotes(facilityId: number): Observable<PermanentPlacementNote> {
        const url = `${this._settings.coreAPIUrl}${PermPostOfferAPIUrls.GetPermPlacementNotesUrl(facilityId)}`;
        return this._http.get<PermanentPlacementNote>(url);
    }

    isPostOfferModal(
        statusId?: SubmittalStatusEnum,
        subStatusId?: SubmittalSubStatusEnum
    ): PostOfferModalDiscoveryResponse {
        if (!statusId || subStatusId) {
            return { isPostOfferModal: false };
        }

        if (statusId === SubmittalStatusEnum.Offer) {
            return { isPostOfferModal: true, postOfferModalType: PostOfferModalType.DecisionPending };
        }

        if (statusId === SubmittalStatusEnum.Placed || statusId === SubmittalStatusEnum.OfferAccepted) {
            return { isPostOfferModal: true, postOfferModalType: PostOfferModalType.DecisionFinalized };
        }

        return { isPostOfferModal: false };
    }

    isPostOfferInterviewModal(statusId?: SubmittalStatusEnum, subStatusId?: SubmittalSubStatusEnum): boolean {
        if (statusId !== SubmittalStatusEnum.Interview) {
            return false;
        }

        if (
            !subStatusId ||
            subStatusId === SubmittalInterviewSubStatusEnum.CompletedPendingDecision ||
            subStatusId === SubmittalInterviewSubStatusEnum.Scheduled
        ) {
            return true;
        }
        return false;
    }

    updateOfferInformation(
        submittalInformation: PermPostOfferWorkflowSubmittalInformationExpanded,
        postOfferUserUpdatedInformation: PermPostOfferUpdateOfferPayload,
        legacyStatusId: SubmittalJobStatus | undefined,
        postOfferModalMetadata: PostOfferModalMetadata | undefined
    ): Observable<SetPermanentJobSubmittalToOfferResultDto> {
        const request: SetPermanentJobSubmittalToOfferDto = {
            jobOrVendorSubmittalId: submittalInformation.uniqueCandidateAndJobApplicationId,
            candidateId: submittalInformation.candidateId,
            submittalType: submittalInformation.submittalType,
            offerDate: postOfferUserUpdatedInformation.offerDate,
            startDate: postOfferUserUpdatedInformation.startDate,
            billingAmount: postOfferUserUpdatedInformation.billingAmount,
            clientNote: postOfferUserUpdatedInformation.liveListNote
        };

        if (postOfferUserUpdatedInformation.removalReason) {
            request.removalReason = postOfferUserUpdatedInformation.removalReason.removalReasonId;
        }

        if (legacyStatusId) {
            // legacy workflow does not allow users to update the joboffer status Id
            request.oldStatusId = legacyStatusId;
        } else if (postOfferModalMetadata?.wasTriggeredFromPlacedOption) {
            // if placed was selected, then the offer modal does not allow placed to be changed
            request.offerStatusId = PermanentJobOfferStatusEnum.Placed;
        } else {
            request.offerStatusId = postOfferUserUpdatedInformation.jobOfferStatus;
        }

        const url = `${this._settings.coreAPIUrl}${PermPostOfferAPIUrls.SetOfferInformationUrl(submittalInformation.submittalId, submittalInformation.jobId)}`;
        return this._http.post<SetPermanentJobSubmittalToOfferResultDto>(url, request);
    }

    getOfferInformation(
        submittalJobOrVendorSubmittalId: number,
        submittalType: SubmittalTypeEnum
    ): Observable<PostOfferInformationSerializable> {
        const url = `${this._settings.coreAPIUrl}${PermPostOfferAPIUrls.GetOfferInformationUrl(submittalJobOrVendorSubmittalId, submittalType)}`;
        return this._http.get<PostOfferInformationSerializable>(url);
    }

    getPermSubmittalData(
        submittalData: SubmittalData,
        submittalJob: AMSubmittalJob,
        isLegacyStatus = false,
        submittalStatusIdOverride: SubmittalJobStatus | null = null
    ): PermPostOfferInterviewModel {
        const response: PermPostOfferInterviewModel = {
            isLegacyStatus,
            facilityName: submittalData.facilityName,
            facilityLink: PermPostOfferLinks.FacilityLink(submittalData.facilityId),
            candidateName: `${submittalData.candidateFirstName} ${submittalData.candidateLastName}`,
            candidateLink: PermPostOfferLinks.CandidateLink(submittalData.submittalType, submittalData.candidateId),
            jobName: submittalJob.title,
            jobLink: PermPostOfferLinks.JobLink(submittalJob.jobId),
            submittalJobId: submittalJob.id,
            submittalType: submittalData.submittalType,
            submittalId: submittalJob.submittalId,
            jobId: submittalJob.jobId,
            candidateId: submittalData.candidateId
        };

        if (isLegacyStatus && submittalStatusIdOverride) {
            //only needed by legacy flow
            response.submittalJobStatusId = submittalStatusIdOverride;
        }

        return response;
    }

    updatePermanentJobOfferStatus(
        jobOfferId: number,
        jobOfferStatus: PermanentJobOfferDashboardStatusEnum
    ): Observable<Response> {
        const url = `${this._settings.coreAPIUrl}${PermPostOfferAPIUrls.PermanentJobOfferUrl(jobOfferId)}`;
        const dto: UpdatePermanentJobOfferStatusDto = { permanentJobOfferDashboardStatusId: jobOfferStatus };

        return this._http.post<Response>(url, dto);
    }

    updatePermPostOfferBillingInfo(data: PostOfferBillingInformation): Observable<PostOfferBillingInformation> {
        const url = `${this._settings.coreAPIUrl}${PermPostOfferAPIUrls.UpdatePermPostOfferBillingInfo()}`;
        return this._http.put<PostOfferBillingInformation>(url, data);
    }

    constructor(
        private readonly _http: HttpClient,
        @Inject(APP_CONFIG) private readonly _settings: Settings
    ) {}
}
